import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { baseOccurrences, businessKindFor, mergeCalendar, type CalendarItem, type CalendarRow, type BusinessKind } from "@/lib/retailCalendarData";

export const CALENDAR_CHANGED_EVENT = "rl-calendar-changed";

export interface RetailCalendarState {
  items: CalendarItem[];
  rows: CalendarRow[];
  kind: BusinessKind;
  sector: string;
  region: string;
  city: string;
  loading: boolean;
  reload: () => Promise<void>;
}

/** Calendario del usuario: base oficial + sus filas, en la ventana [hoy, hoy+days]. */
/** `fromIso` (YYYY-MM-DD) permite ver otra ventana, p. ej. el mes que se mira en la cuadrícula. */
export function useRetailCalendar(userId: string | null, days = 30, fromIso?: string): RetailCalendarState {
  const [rows, setRows] = useState<CalendarRow[]>([]);
  const [profile, setProfile] = useState({ sector: "", region: "", city: "" });
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!userId) return;
    const [{ data: r }, { data: p }] = await Promise.all([
      supabase.from("user_calendar_events").select("id, system_key, date_start, date_end, title, category, reason, notes, source, status, lead_days, color").eq("user_id", userId),
      supabase.from("profiles").select("retail_sector, comunidad_autonoma, city").eq("user_id", userId).maybeSingle(),
    ]);
    setRows((r ?? []) as CalendarRow[]);
    setProfile({ sector: (p as any)?.retail_sector ?? "", region: (p as any)?.comunidad_autonoma ?? "", city: (p as any)?.city ?? "" });
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    void reload();
    const h = () => void reload();
    window.addEventListener(CALENDAR_CHANGED_EVENT, h);
    return () => window.removeEventListener(CALENDAR_CHANGED_EVENT, h);
  }, [reload]);

  const kind = businessKindFor(profile.sector);
  const items = useMemo(() => {
    const now = fromIso ? new Date(`${fromIso}T00:00:00Z`) : new Date();
    return mergeCalendar(baseOccurrences(now, days, kind, profile.region), rows, now, days);
  }, [rows, kind, profile.region, days, fromIso]);

  return { items, rows, kind, ...profile, loading, reload };
}
